/**
 * table-sort — client-side column sorting for the hand-rolled list
 * tables (Contacts, Companies).
 *
 * Client-side on purpose: both lists already fetch ≤500 rows and
 * filter in a useMemo, so a server round-trip per header click would
 * buy nothing until real pagination exists. Sorting applies AFTER
 * filtering and BEFORE selection sees the visible rows.
 *
 * Null/undefined/blank values sort LAST in both directions — "no
 * value" is noise the user is never looking for, not a smallest value.
 */

import { useCallback, useState } from 'react';
import type { ReactNode } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';

export type SortDirection = 'asc' | 'desc';

export type SortValue = string | number | Date | null | undefined;

/** True for the values that always sort last regardless of direction. */
function isEmpty(v: SortValue): boolean {
  return v === null || v === undefined || (typeof v === 'string' && v.trim() === '');
}

/**
 * Compares two cell values for a given direction. Strings compare
 * case-insensitively via localeCompare; numbers numerically; Dates by
 * epoch. Mixed types (a data bug, not a real case) coerce to string.
 */
export function compareValues(a: SortValue, b: SortValue, direction: SortDirection): number {
  const aEmpty = isEmpty(a);
  const bEmpty = isEmpty(b);
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1; // empty last, independent of direction
  if (bEmpty) return -1;
  let cmp: number;
  if (typeof a === 'number' && typeof b === 'number') {
    cmp = a - b;
  } else if (a instanceof Date && b instanceof Date) {
    cmp = a.getTime() - b.getTime();
  } else {
    cmp = String(a).localeCompare(String(b), undefined, { sensitivity: 'base' });
  }
  return direction === 'asc' ? cmp : -cmp;
}

/**
 * Sorts rows by an accessor. Stable (Array.prototype.sort is stable in
 * every supported runtime), non-mutating.
 */
export function sortRows<T>(
  rows: readonly T[],
  accessor: (row: T) => SortValue,
  direction: SortDirection,
): T[] {
  return [...rows].sort((a, b) => compareValues(accessor(a), accessor(b), direction));
}

export interface TableSortState<K extends string> {
  /** Active sort column, or null for the server's natural order. */
  sortKey: K | null;
  direction: SortDirection;
  /** Header-click handler: new column → asc; same column → flip. */
  toggle: (key: K) => void;
}

export function useTableSort<K extends string>(): TableSortState<K> {
  // One state value + a PURE updater. Dispatching a second setState
  // from inside an updater is a side effect — under React.StrictMode's
  // dev double-invocation the queued asc↔desc flip would compose to a
  // no-op, so key and direction must transition together.
  const [state, setState] = useState<{ sortKey: K | null; direction: SortDirection }>({
    sortKey: null,
    direction: 'asc',
  });
  const toggle = useCallback((key: K) => {
    setState((current) =>
      current.sortKey === key
        ? { sortKey: key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { sortKey: key, direction: 'asc' },
    );
  }, []);
  return { sortKey: state.sortKey, direction: state.direction, toggle };
}

interface SortableThProps {
  label: ReactNode;
  active: boolean;
  direction: SortDirection;
  onClick: () => void;
  className?: string;
}

/**
 * Clickable header cell with an aria-sort attribute and an arrow
 * indicator (neutral double-arrow when inactive).
 */
export function SortableTh({ label, active, direction, onClick, className }: SortableThProps) {
  const ariaSort = active ? (direction === 'asc' ? 'ascending' : 'descending') : undefined;
  const Icon = active ? (direction === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <th aria-sort={ariaSort} className={className}>
      <button
        type="button"
        onClick={onClick}
        className="inline-flex items-center gap-1 hover:text-foreground"
      >
        {label}
        <Icon
          className={`h-3 w-3 ${active ? '' : 'text-muted-foreground/50'}`}
          aria-hidden="true"
        />
      </button>
    </th>
  );
}
